// src/views/admin/PredictiveHealthDashboard.js
// Predictive Maintenance Dashboard with live asset performance metrics + live AI insights
// Version: 3.0 — Enterprise UI overhaul: debounced search, live WS badge, critical alert banner,
//   health distribution bar, bulk selection, keyboard navigation, jump-to-page pagination,
//   React.memo + displayName on all sub-components, performance-optimised re-render guards.
// ----------------------------------------------------------------------------
// MERGE NOTES (v2.3 → v3.0):
//   PERFORMANCE:
//   - useDebounce hook (300 ms) gates the filter/sort useMemo; eliminates
//     per-keystroke recalculation over large datasets
//   - All sub-components wrapped in React.memo with displayName set for
//     React DevTools profiler clarity
//   - summaryStats, groupedAssets, paginatedAssets each isolated in their own
//     useMemo so an unrelated state change never triggers all three
//   - handleSort, handlePageChange, handleSettingsChange, handleExport all in
//     useCallback — stable references prevent child re-renders
//
//   ENTERPRISE UI:
//   - CriticalAlertBanner: dismissible sticky banner when critical > 0,
//     auto-dismissed after data refresh
//   - LiveConnectionBadge: visual WS indicator (connected / connecting /
//     disconnected) wired to webSocketService subscribe/unsubscribe lifecycle
//   - HealthDistributionBar: full-width segmented progress bar below KPI row
//     shows healthy / warning / critical share at a glance
//   - Bulk selection: checkbox column in table view + floating action bar
//     for exporting only the selected rows
//   - Keyboard navigation: ArrowUp / ArrowDown move asset selection through
//     the current page; Escape clears selection
//   - Jump-to-page input in pagination footer
//   - Filter Select replaced with ButtonGroup badges showing live counts for
//     All / Critical / Warning / Healthy — faster scanning, fewer clicks
//   - SortableTh extracted as a shared memo sub-component (DRY)
//   - AssetHealthCard enhanced: inline health Progress bar + pulse ring on
//     critical, right-side mini circular ring replaced with segmented bar
//
//   CORE LOGIC: ZERO CHANGES — all fetchData, WebSocket, analyticsService
//   calls, sort/filter/pagination logic, SettingsDrawer, AssetDetailView,
//   export helpers, getAssetStatus, calculateTrend, detectAnomalies are
//   bit-for-bit identical to v2.3.
// ----------------------------------------------------------------------------
// --- UPDATE (round 3: live + real data + AI) --------------------------------------------------------
// • The live badge always said "Live" (it was set to connected as soon as subscribe() returned);
//   it now follows the websocket's real status (connected / reconnecting / offline).
// • It listened only to 'asset_health_events', which nothing emits, and read payload.asset_id from
//   the { type, payload } frame (always undefined). It now listens to the events the backend sends:
//   asset_health_scores_updated / predictive_alerts_updated / predictive_analysis_complete → quiet
//   refetch (debounced, no full-screen spinner); asset_prediction_update / predictive_risk_update →
//   the asset's AI fields (RUL days, risk, status) update in place and the card flashes.
//   Props from the Analytics Hub (liveVersion / lastLiveEvent) are handled the same way.
// • Retry reloaded the browser page — it refetches.
// • The AI card showed an invented confidence (92 % / 85 %) and today's date when the model had
//   produced no prediction; it shows "—" and "no prediction yet". Saving a prediction no longer
//   writes 0 for values the model did not return.
// • AI strip: production RUL model version and MAE (live), "Run AI analysis" (scores every asset
//   with the RUL model and creates preventive work orders), Explain and Ask FlowSense AI.
// --- END UPDATE ------------------------------------------------------------------------------------

import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Heading, Spinner, Alert, AlertIcon, AlertTitle, AlertDescription,
  VStack, SimpleGrid, Text, useColorModeValue, Flex, HStack, Button,
  useToast, Icon, Badge, Select, InputGroup, InputLeftElement, Input,
  Progress, Stat, StatLabel, StatNumber, StatHelpText, Divider,
  Container, Tag, TagLabel, TagLeftIcon, IconButton, CircularProgress,
  CircularProgressLabel, Skeleton, Tooltip, Menu, MenuButton, MenuList,
  MenuItem, MenuDivider, Drawer, DrawerBody, DrawerHeader, DrawerOverlay,
  DrawerContent, DrawerCloseButton, useDisclosure, Tabs, TabList, TabPanels,
  Tab, TabPanel, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Switch,
  FormControl, FormLabel, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, Slider, SliderTrack, SliderFilledTrack,
  SliderThumb, useClipboard, Grid, GridItem, ButtonGroup, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  ModalCloseButton, CloseButton, Checkbox, ScaleFade,
} from '@chakra-ui/react';
import {
  FaSync, FaSearch, FaFilter, FaExclamationTriangle, FaCheckCircle,
  FaBolt, FaHeartbeat, FaRobot, FaChartLine, FaArrowRight,
  FaTimesCircle, FaChevronLeft, FaChevronRight, FaDatabase,
  FaFileExport, FaCog, FaLayerGroup,
  FaThLarge, FaList, FaTable, FaSortAmountDown, FaSortAmountUp,
  FaSort, FaSortUp, FaSortDown, FaCopy, FaTachometerAlt,
  FaExclamationCircle, FaInfoCircle, FaLightbulb, FaHistory, FaChartBar,
  FaChartPie, FaChartArea, FaEye, FaEyeSlash, FaMapMarkerAlt,
  FaWrench, FaClock, FaFire, FaCloudUploadAlt, FaFileDownload,
  FaArrowUp, FaArrowDown, FaKeyboard, FaCheckSquare,
} from 'react-icons/fa';
import { FiWifi, FiWifiOff, FiActivity } from 'react-icons/fi';
import ReactApexChart from 'react-apexcharts';
import { motion, AnimatePresence } from 'framer-motion';
import analyticsService from '../../services/analyticsService';

// --- CORE: Import FlowSense WebSocket service for live asset health events ---
import webSocketService from '../../services/websocketService';
// --- END CORE ---
// --- UPDATE (round 3) ---
import { runAnalysis as runPredictiveAnalysis } from '../../services/predictiveFailureService';
import { useMlOverview } from '../../hooks/useMlOps';
import { ExplainButton } from '../ml/FlowSenseAIExplainer';
import { currentUser, fmtMetric, timeAgo } from '../ml/mlFormat';

const REFETCH_EVENTS = new Set(['asset_health_scores_updated', 'predictive_alerts_updated', 'predictive_analysis_complete', 'rul_batch_complete']);
const PATCH_EVENTS = new Set(['asset_prediction_update', 'predictive_risk_update']);
const unwrapFrame = (msg) => (msg && typeof msg === 'object' && 'payload' in msg && msg.payload && typeof msg.payload === 'object' ? msg.payload : msg);
const wsStatusOf = (s) => (s === 'connected' ? 'connected' : (s === 'connecting' || s === 'reconnecting' || s === 'waiting') ? 'connecting' : 'disconnected');
// --- END UPDATE ---

// ============================================================================
// CONSTANTS
// ============================================================================

const HEALTH_THRESHOLDS = { CRITICAL: 50, WARNING: 75 };

const STATUS_META = {
  critical: { color: 'red',    label: 'Critical', icon: FaTimesCircle,     hex: '#F56565', gradient: 'linear(to-r, red.400, red.600)'    },
  warning:  { color: 'orange', label: 'Warning',  icon: FaExclamationTriangle, hex: '#ED8936', gradient: 'linear(to-r, orange.400, orange.600)' },
  healthy:  { color: 'green',  label: 'Healthy',  icon: FaCheckCircle,     hex: '#48BB78', gradient: 'linear(to-r, green.400, green.600)'  },
};

const WS_STATUS = { CONNECTED: 'connected', CONNECTING: 'connecting', DISCONNECTED: 'disconnected' };

// ============================================================================
// HOOKS
// ============================================================================

/** Debounce a rapidly-changing value to avoid per-keystroke recalculation. */
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// ============================================================================
// UTILITY HELPERS  (🔒 CORE — UNCHANGED FROM v2.3)
// ============================================================================

const getAssetStatus = (score) => {
  if (score < HEALTH_THRESHOLDS.CRITICAL) return { ...STATUS_META.critical };
  if (score < HEALTH_THRESHOLDS.WARNING)  return { ...STATUS_META.warning  };
  return { ...STATUS_META.healthy };
};

const exportToCSV = (data, filename = 'asset-health-export.csv') => {
  const headers = ['Asset ID','Name','Type','Location','Health Score','Status','Recommendation'];
  const rows    = data.map(asset => [
    asset.asset_id || asset.id, asset.name, asset.type,
    asset.location || 'N/A', asset.health_score,
    getAssetStatus(asset.health_score).label, asset.recommendation || 'N/A',
  ]);
  const csv  = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url  = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToJSON = (data, filename = 'asset-health-export.json') => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const link = document.createElement('a');
  const url  = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const calculateTrend = (history) => {
  if (!history || history.length < 2) return 0;
  const recent     = history.slice(-5);
  const older      = history.slice(0, Math.min(5, history.length - 5));
  const recentAvg  = recent.reduce((s, h) => s + h.health_score, 0) / recent.length;
  const olderAvg   = older.reduce((s, h)  => s + h.health_score, 0) / older.length;
  return recentAvg - olderAvg;
};

const detectAnomalies = (history) => {
  if (!history || history.length < 3) return [];
  const scores = history.map(h => h.health_score);
  const mean   = scores.reduce((a, b) => a + b, 0) / scores.length;
  const stdDev = Math.sqrt(scores.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / scores.length);
  return history
    .filter(h => Math.abs(h.health_score - mean) > 2 * stdDev)
    .map(h => ({ ...h, deviation: ((h.health_score - mean) / stdDev).toFixed(2) }));
};

// ============================================================================
// ANIMATION VARIANTS  (🔒 CORE — UNCHANGED FROM v2.3)
// ============================================================================

const MotionBox  = motion(Box);

const listVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemVariants = {
  hidden:  { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 100, damping: 15 } },
};
const detailVariants = {
  hidden:  { opacity: 0, y: 20, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: 'easeOut' } },
};
const pulseVariant = {
  initial: { scale: 1 },
  animate: { scale: [1, 1.05, 1], transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
};

// ============================================================================
// NEW SUB-COMPONENTS  (🚀 ENTERPRISE ADDITIONS)
// ============================================================================

/** Sortable <Th> — extracted so table-view and detail tabs share one definition. */
const SortableTh = React.memo(({ label, field, sortBy, sortOrder, onSort, isNumeric, tooltip }) => {
  const activeColor = useColorModeValue('blue.600', 'blue.300');
  const mutedColor  = useColorModeValue('gray.400', 'gray.500');
  const isActive    = sortBy === field;

  const icon = isActive
    ? (sortOrder === 'asc' ? <Icon as={FaSortUp}   ml={1} boxSize={3} color={activeColor} />
                           : <Icon as={FaSortDown} ml={1} boxSize={3} color={activeColor} />)
    : <Icon as={FaSort} ml={1} boxSize={3} color={mutedColor} />;

  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(field)}
      color={isActive ? activeColor : undefined}
      _hover={{ color: activeColor }}
      whiteSpace="nowrap"
    >
      <Tooltip label={tooltip || `Sort by ${label}`} placement="top" hasArrow>
        <HStack spacing={0} justify={isNumeric ? 'flex-end' : 'flex-start'} display="inline-flex">
          <Text>{label}</Text>{icon}
        </HStack>
      </Tooltip>
    </Th>
  );
});
SortableTh.displayName = 'SortableTh';

// ─────────────────────────────────────────────────────────────────────────────
/** Dismissible banner that fires when critical assets exist. */
const CriticalAlertBanner = React.memo(({ count, onNavigate, onDismiss }) => {
  const bg = useColorModeValue('red.50', 'rgba(254,178,178,0.12)');

  if (count === 0) return null;

  return (
    <ScaleFade initialScale={0.95} in>
      <Flex
        align="center"
        justify="space-between"
        bg={bg}
        borderWidth="1px"
        borderColor="red.300"
        borderRadius="xl"
        px={5}
        py={3}
        mb={4}
        gap={3}
        flexWrap="wrap"
      >
        <HStack spacing={3}>
          <MotionBox
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Icon as={FaFire} color="red.500" boxSize={5} />
          </MotionBox>
          <Box>
            <Text fontWeight="bold" color="red.700" fontSize="sm">
              {count} Asset{count > 1 ? 's' : ''} Require Immediate Attention
            </Text>
            <Text fontSize="xs" color="red.500">
              Critical health scores detected — review and schedule maintenance now.
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2}>
          <Button
            size="xs"
            colorScheme="red"
            variant="solid"
            rightIcon={<Icon as={FaArrowRight} />}
            onClick={onNavigate}
          >
            View Critical
          </Button>
          <CloseButton size="sm" color="red.400" onClick={onDismiss} />
        </HStack>
      </Flex>
    </ScaleFade>
  );
});
CriticalAlertBanner.displayName = 'CriticalAlertBanner';

// ─────────────────────────────────────────────────────────────────────────────
/** WebSocket live-connection badge — mirrors FlowSense WorkOrderPage pattern. */
const LiveConnectionBadge = React.memo(({ status }) => {
  const meta = {
    [WS_STATUS.CONNECTED]:    { color: 'green',  icon: FiWifi,     label: 'Live',        dot: 'green.400'  },
    [WS_STATUS.CONNECTING]:   { color: 'yellow', icon: FiActivity, label: 'Connecting',  dot: 'yellow.400' },
    [WS_STATUS.DISCONNECTED]: { color: 'gray',   icon: FiWifiOff,  label: 'Offline',     dot: 'gray.400'   },
  };
  const m = meta[status] || meta[WS_STATUS.DISCONNECTED];

  return (
    <Tooltip label={`WebSocket: ${m.label}`} hasArrow placement="bottom">
      <HStack
        spacing={1}
        px={2}
        py={1}
        borderRadius="full"
        borderWidth="1px"
        borderColor={`${m.color}.300`}
        bg={`${m.color}.50`}
        cursor="default"
      >
        {status === WS_STATUS.CONNECTED && (
          <MotionBox
            w={2} h={2} borderRadius="full" bg={m.dot}
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
        {status !== WS_STATUS.CONNECTED && (
          <Box w={2} h={2} borderRadius="full" bg={m.dot} />
        )}
        <Icon as={m.icon} boxSize={3} color={`${m.color}.500`} />
        <Text fontSize="10px" fontWeight="bold" color={`${m.color}.700`} display={{ base: 'none', md: 'block' }}>
          {m.label}
        </Text>
      </HStack>
    </Tooltip>
  );
});
LiveConnectionBadge.displayName = 'LiveConnectionBadge';

// ─────────────────────────────────────────────────────────────────────────────
/** Full-width colour-segmented bar showing healthy / warning / critical split. */
const HealthDistributionBar = React.memo(({ healthy, warning, critical, total }) => {
  if (total === 0) return null;
  const hp = ((healthy  / total) * 100).toFixed(1);
  const wp = ((warning  / total) * 100).toFixed(1);
  const cp = ((critical / total) * 100).toFixed(1);

  return (
    <Tooltip
      label={`Healthy: ${hp}% · Warning: ${wp}% · Critical: ${cp}%`}
      hasArrow
    >
      <Box borderRadius="full" overflow="hidden" h="6px" w="full" cursor="default">
        <Flex h="full">
          <Box flex={hp} bg="green.400"  transition="flex 0.5s ease" />
          <Box flex={wp} bg="orange.400" transition="flex 0.5s ease" />
          <Box flex={cp} bg="red.400"    transition="flex 0.5s ease" />
        </Flex>
      </Box>
    </Tooltip>
  );
});
HealthDistributionBar.displayName = 'HealthDistributionBar';

// ─────────────────────────────────────────────────────────────────────────────
/** Filter toggle buttons with live counts. */
const StatusFilterButtons = React.memo(({ value, onChange, counts }) => {
  const options = [
    { key: 'all',      label: 'All',      scheme: 'blue',   count: counts.total    },
    { key: 'critical', label: 'Critical', scheme: 'red',    count: counts.critical },
    { key: 'warning',  label: 'Warning',  scheme: 'orange', count: counts.warning  },
    { key: 'healthy',  label: 'Healthy',  scheme: 'green',  count: counts.healthy  },
  ];

  return (
    <HStack spacing={1} flexWrap="wrap">
      {options.map(opt => (
        <Button
          key={opt.key}
          size="xs"
          borderRadius="full"
          variant={value === opt.key ? 'solid' : 'outline'}
          colorScheme={value === opt.key ? opt.scheme : 'gray'}
          onClick={() => onChange(opt.key)}
          rightIcon={
            <Badge
              colorScheme={value === opt.key ? 'whiteAlpha' : opt.scheme}
              variant={value === opt.key ? 'solid' : 'subtle'}
              fontSize="9px"
              borderRadius="full"
              px={1}
              ml={-1}
            >
              {opt.count}
            </Badge>
          }
        >
          {opt.label}
        </Button>
      ))}
    </HStack>
  );
});
StatusFilterButtons.displayName = 'StatusFilterButtons';

// ============================================================================
// ASSET HEALTH CARD  (🚀 Enhanced — adds inline progress bar + pulse ring)
// ============================================================================

// --- UPDATE (round 3): isFlashing (live AI update) + the asset's live AI prediction line
const AiPredictionLine = ({ asset }) => {
  if (asset.ai_rul_days == null && asset.ai_risk_score == null && !asset.ai_health_status) return null;
  const scheme = asset.ai_health_status === 'critical' ? 'red' : asset.ai_health_status === 'warning' ? 'orange' : 'purple';
  return (
    <HStack spacing={1} mt={1} fontSize="10px" flexWrap="wrap">
      <Badge colorScheme={scheme} fontSize="9px">AI</Badge>
      {asset.ai_rul_days != null && <Text>{asset.ai_rul_days} days left</Text>}
      {asset.ai_risk_score != null && <Text>· risk {asset.ai_risk_score}</Text>}
    </HStack>
  );
};
const AssetHealthCard = React.memo(({ asset, onClick, isSelected, viewMode = 'card', isComparing, onToggleCompare, isFlashing = false }) => {
  const { color, label, gradient } = getAssetStatus(asset.health_score);
  const bg          = useColorModeValue('white', 'gray.800');
  const selectedBg  = useColorModeValue('blue.50', 'rgba(66,153,225,0.1)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const isCritical  = asset.health_score < HEALTH_THRESHOLDS.CRITICAL;

  if (viewMode === 'list') {
    return (
      <MotionBox
        layout
        variants={itemVariants}
        whileHover={{ x: 4 }}
        bg={isSelected ? selectedBg : bg}
        borderWidth="1px"
        borderColor={isFlashing ? 'purple.400' : isSelected ? 'blue.400' : borderColor}
        boxShadow={isFlashing ? '0 0 0 2px var(--chakra-colors-purple-300)' : undefined}
        borderLeftWidth="4px"
        borderLeftColor={`${color}.400`}
        borderRadius="md"
        onClick={() => onClick(asset)}
        cursor="pointer"
        p={3}
        position="relative"
      >
        <Flex justify="space-between" align="center">
          <HStack flex={1} spacing={4}>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{asset.name}</Text>
              <HStack spacing={2}>
                <Badge colorScheme="gray" fontSize="9px">{asset.type}</Badge>
                {asset.location && (
                  <Text fontSize="9px" color="gray.500">
                    <Icon as={FaMapMarkerAlt} mr={1} />{asset.location}
                  </Text>
                )}
              </HStack>
              <AiPredictionLine asset={asset} />
            </VStack>
          </HStack>
          <HStack spacing={3}>
            {/* Enhanced: inline segmented bar replacing the plain number */}
            <VStack spacing={1} align="end" minW="56px">
              <Text fontSize="xs" fontWeight="bold" color={`${color}.500`}>{asset.health_score}</Text>
              <Progress
                value={asset.health_score}
                colorScheme={color}
                size="xs"
                w="56px"
                borderRadius="full"
                bg="gray.100"
              />
            </VStack>
            {isComparing && (
              <Checkbox
                colorScheme="blue"
                size="md"
                isChecked={false}
                onClick={e => { e.stopPropagation(); onToggleCompare?.(asset); }}
              />
            )}
          </HStack>
        </Flex>
      </MotionBox>
    );
  }

  return (
    <MotionBox
      layout
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      bg={isSelected ? selectedBg : bg}
      borderWidth="1px"
      borderColor={isFlashing ? 'purple.400' : isSelected ? 'blue.400' : borderColor}
      boxShadow={isFlashing ? '0 0 0 2px var(--chakra-colors-purple-300)' : undefined}
      borderLeftWidth="4px"
      borderLeftColor={`${color}.400`}
      borderRadius="md"
      onClick={() => onClick(asset)}
      cursor="pointer"
      whileHover={{ scale: 1.01, boxShadow: 'lg' }}
      whileTap={{ scale: 0.99 }}
      p={3}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""', position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
        bgGradient: isSelected ? gradient : 'none', opacity: 0.05, pointerEvents: 'none',
      }}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{asset.name}</Text>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
            {asset.type}
          </Text>
        </VStack>
        <VStack align="end" spacing={0} pl={2} flexShrink={0}>
          {/* 🚀 Critical assets get an animated pulse ring */}
          {isCritical ? (
            <MotionBox variants={pulseVariant} initial="initial" animate="animate">
              <Text fontSize="xl" fontWeight="800" color={`${color}.500`} lineHeight={1}>
                {asset.health_score}
              </Text>
            </MotionBox>
          ) : (
            <Text fontSize="xl" fontWeight="800" color={`${color}.500`} lineHeight={1}>
              {asset.health_score}
            </Text>
          )}
          <Text fontSize="10px" fontWeight="bold" color={`${color}.500`} textTransform="uppercase">
            {label}
          </Text>
        </VStack>
      </Flex>

      <AiPredictionLine asset={asset} />
      {/* 🚀 Inline health progress bar — immediate visual density */}
      <Progress
        value={asset.health_score}
        colorScheme={color}
        size="xs"
        borderRadius="full"
        bg="gray.100"
        hasStripe={isCritical}
        isAnimated={isCritical}
      />

      {isComparing && (
        <Checkbox
          position="absolute" top={2} right={2}
          colorScheme="blue"
          onClick={e => { e.stopPropagation(); onToggleCompare?.(asset); }}
        />
      )}
    </MotionBox>
  );
});
AssetHealthCard.displayName = 'AssetHealthCard';

// ============================================================================
// ASSET DETAIL VIEW  (🔒 CORE — UNCHANGED FROM v2.3, minor theme polish only)
// ============================================================================

const AssetDetailView = React.memo(({ asset }) => {
  const [history,           setHistory]           = useState([]);
  const [perfData,          setPerfData]          = useState(null);
  const [latestPrediction,  setLatestPrediction]  = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [savingPrediction,  setSavingPrediction]  = useState(false);
  const [prediction,        setPrediction]        = useState(null);
  const [anomalies,         setAnomalies]         = useState([]);
  const [showAnomalies,     setShowAnomalies]     = useState(false);
  const [activeMetricTab,   setActiveMetricTab]   = useState(0);

  // All useColorModeValue calls hoisted (Rules of Hooks)
  const textColor           = useColorModeValue('gray.700', 'gray.200');
  const borderColor         = useColorModeValue('gray.200', 'gray.700');
  const tooltipTheme        = useColorModeValue('light', 'dark');
  const factorsBg           = useColorModeValue('gray.50', 'gray.700');
  const emptyChartBg        = useColorModeValue('gray.50', 'gray.900');
  const aiCardBg            = useColorModeValue('blue.50', 'rgba(49,130,206,0.1)');
  const aiIconColor         = useColorModeValue('blue.100', 'whiteAlpha.100');
  const anomalyBg           = useColorModeValue('red.50', 'rgba(245,101,101,0.1)');
  const cardHeaderBg        = useColorModeValue('white', 'gray.800');
  const predictionCardBg    = useColorModeValue('white', 'gray.800');
  const anomalyItemBg       = useColorModeValue('white', 'gray.800');
  const accordionExpandedBg = useColorModeValue('blue.50', 'blue.900');

  const toast = useToast();
  const { color, label, icon, hex, gradient } = getAssetStatus(asset.health_score);
  const { hasCopied, onCopy } = useClipboard(JSON.stringify(asset, null, 2));

  // 3-way concurrent fetch (🔒 CORE — UNCHANGED)
  useEffect(() => {
    const fetchDetails = async () => {
      if (!asset) return;
      setLoading(true);
      setPerfData(null);
      setLatestPrediction(null);
      try {
        const id = asset.asset_id || asset.id;
        const [historyRes, perfRes, predRes] = await Promise.allSettled([
          analyticsService.getAssetHealthHistory(id),
          analyticsService.getAssetPerformance
            ? analyticsService.getAssetPerformance(id)
            : Promise.resolve(null),
          analyticsService.getLatestAssetPrediction
            ? analyticsService.getLatestAssetPrediction(id)
            : Promise.resolve(null),
        ]);
        if (historyRes.status === 'fulfilled' && historyRes.value) {
          const historyData = historyRes.value.data || [];
          setHistory(historyData);
          setAnomalies(detectAnomalies(historyData));
        } else {
          setHistory([]); setAnomalies([]);
        }
        if (perfRes.status === 'fulfilled' && perfRes.value?.success) setPerfData(perfRes.value.data);
        // --- UPDATE: only a real prediction row counts (an empty { success, data: null } envelope is not one)
        if (predRes.status === 'fulfilled' && predRes.value) {
          const p = predRes.value.data !== undefined ? predRes.value.data : predRes.value;
          if (p && (p.prediction_date || p.predicted_rul_days != null || p.health_status)) setLatestPrediction(p);
        }
        // --- END UPDATE
      } catch (error) {
        console.error('Failed to fetch asset details', error);
        setHistory([]); setAnomalies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
    setPrediction(null);
  }, [asset]);

  const handleGeneratePrediction = async () => {
    setPredictionLoading(true);
    try {
      const id       = asset.asset_id || asset.id;
      const response = await analyticsService.predictAssetFailure(id);
      const predData = response.data || response;
      if (predData) {
        setPrediction(predData);
        setLatestPrediction(predData);
        toast({ title: 'Analysis Complete', description: 'AI prediction model has successfully run.', status: 'success', duration: 3000, position: 'top-right', isClosable: true });
      }
    } catch (error) {
      toast({ title: 'Prediction Failed', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setPredictionLoading(false);
    }
  };

  const handleSavePrediction = async () => {
    if (!prediction) return;
    setSavingPrediction(true);
    try {
      const id = asset.asset_id || asset.id;
      const payload = {
        asset_id:           id,
        // --- UPDATE: values the model did not return are sent as null, not 0
        predicted_rul_days: prediction.predicted_rul_days ?? prediction.days_to_failure ?? null,
        confidence_score:   prediction.confidence_score ?? prediction.confidence ?? null,
        health_status:      prediction.health_status || (asset.health_score < 50 ? 'critical' : asset.health_score < 75 ? 'warning' : 'good'),
        risk_score:         prediction.risk_score ?? prediction.risk ?? null,
        // --- END UPDATE
        prediction_date:    new Date().toISOString(),
      };
      let response;
      if (analyticsService.saveAssetPrediction) {
        response = await analyticsService.saveAssetPrediction(payload);
      } else {
        throw new Error('Saving predictions is not available in this build.'); // --- UPDATE: no fake success
      }
      if (response && (response.success || response.id)) {
        toast({ title: 'Prediction Saved', description: 'The AI analysis has been recorded in the database.', status: 'success', duration: 3000, isClosable: true });
      }
    } catch (error) {
      toast({ title: 'Save Failed', description: 'Could not persist prediction data.', status: 'error', duration: 3000 });
    } finally {
      setSavingPrediction(false);
    }
  };

  const trend = calculateTrend(history);

  const aiDisplay = useMemo(() => {
    const rawConfidence   = latestPrediction?.confidence_score;
    const confidenceScore = rawConfidence != null
      ? (rawConfidence <= 1 ? rawConfidence * 100 : rawConfidence).toFixed(0)
      : null; // --- UPDATE: no invented confidence
    const predictionDate        = latestPrediction?.prediction_date ? new Date(latestPrediction.prediction_date).toLocaleDateString() : null;
    const riskScore             = latestPrediction?.risk_score;
    const statusBadgeScheme     = latestPrediction?.health_status === 'critical' ? 'red' : 'blue';
    const statusBadgeText       = latestPrediction?.health_status ? `Status: ${latestPrediction.health_status.toUpperCase()}` : 'Smart Insights';
    const confidenceBadgeScheme = confidenceScore === null ? 'gray' : confidenceScore > 90 ? 'green' : 'purple';
    const subtitle              = latestPrediction
      ? `Based on AI RUL model analysis from ${predictionDate || 'an unknown date'}. Risk Score: ${riskScore ?? 'N/A'}/100.`
      : 'No AI prediction yet for this asset — run the predictive model for an assessment.';
    return { confidenceScore, predictionDate, riskScore, statusBadgeScheme, statusBadgeText, confidenceBadgeScheme, subtitle };
  }, [latestPrediction]);

  const chartOptions = {
    chart: { type: 'area', toolbar: { show: true, tools: { download: true, zoom: true, pan: true } }, background: 'transparent', fontFamily: 'inherit', animations: { enabled: true, easing: 'easeinout', speed: 800, animateGradually: { enabled: true, delay: 150 }, dynamicAnimation: { enabled: true, speed: 350 } }, zoom: { enabled: true } },
    colors: [hex],
    fill:   { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.05, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke:     { curve: 'smooth', width: 3 },
    markers: {
      size: showAnomalies && anomalies.length > 0 ? 6 : 0,
      colors: ['#F56565'], strokeColors: '#fff', strokeWidth: 2, hover: { size: 8 },
    },
    annotations: showAnomalies ? {
      points: anomalies.map(a => ({
        x: new Date(a.recorded_at).getTime(), y: a.health_score,
        marker: { size: 8, fillColor: '#F56565', strokeColor: '#fff', strokeWidth: 3 },
        label:  { text: `Anomaly: σ${a.deviation}`, style: { background: '#F56565', color: '#fff', fontSize: '10px' } },
      })),
    } : {},
    xaxis: { type: 'datetime', axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: textColor } } },
    yaxis: { min: 0, max: 100, labels: { style: { colors: textColor } }, title: { text: 'Health Score', style: { color: textColor } } },
    grid:    { borderColor, strokeDashArray: 4 },
    tooltip: { theme: tooltipTheme, x: { format: 'dd MMM yyyy HH:mm' }, y: { formatter: val => `${val} Health Score` } },
  };

  const chartSeries = [{ name: 'Health Score', data: history.map(h => [new Date(h.recorded_at).getTime(), h.health_score]) }];

  const distributionStats = useMemo(() => {
    if (!history || history.length === 0) return { healthy: '0%', warning: '0%', critical: '0%' };
    const total    = history.length;
    const healthy  = history.filter(h => h.health_score >= 75).length;
    const warning  = history.filter(h => h.health_score >= 50 && h.health_score < 75).length;
    const critical = history.filter(h => h.health_score < 50).length;
    return { healthy: `${((healthy/total)*100).toFixed(0)}%`, warning: `${((warning/total)*100).toFixed(0)}%`, critical: `${((critical/total)*100).toFixed(0)}%` };
  }, [history]);

  return (
    <AnimatePresence mode="wait">
      <MotionBox key={asset.asset_id || asset.id} variants={detailVariants} initial="hidden" animate="visible" exit="hidden" h="100%" overflowY="auto" pr={2} className="custom-scrollbar">
        <VStack align="stretch" spacing={6} pb={10}>

          {/* ── HEADER ────────────────────────────────────────────────────── */}
          <Box bg={cardHeaderBg} p={6} borderRadius="xl" shadow="lg" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden"
            _before={{ content: '""', position: 'absolute', top: -2, left: -2, right: -2, height: '4px', bgGradient: gradient, borderRadius: 'full' }}
          >
            <Flex justify="space-between" align="start" wrap="wrap" gap={4}>
              <Box flex={1}>
                <HStack mb={2} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="gray"   fontSize="xs" px={2} py={1} borderRadius="md">ID: {asset.asset_id || asset.id}</Badge>
                  <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">{asset.type}</Badge>
                  {asset.location && (
                    <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="md">
                      <Icon as={FaMapMarkerAlt} mr={1} />{asset.location}
                    </Badge>
                  )}
                </HStack>
                <Heading size="lg" color={textColor} mb={1}>{asset.name}</Heading>
                <HStack spacing={4} flexWrap="wrap">
                  <Tooltip label="Copy Asset Data" hasArrow>
                    <Button size="xs" leftIcon={<Icon as={FaCopy} />} onClick={onCopy} variant="ghost">
                      {hasCopied ? 'Copied!' : 'Copy JSON'}
                    </Button>
                  </Tooltip>
                  <Text fontSize="xs" color="gray.500">Last Updated: {new Date(asset.updated_at || Date.now()).toLocaleString()}</Text>
                </HStack>
              </Box>
              <VStack spacing={4} align="end">
                <HStack spacing={4}>
                  <VStack align="end" spacing={0}>
                    <Text fontSize="sm" color="gray.500">Current Status</Text>
                    <Tag size="lg" variant="subtle" colorScheme={color} borderRadius="full">
                      <TagLeftIcon boxSize="12px" as={icon} />
                      <TagLabel fontWeight="bold">{label}</TagLabel>
                    </Tag>
                  </VStack>
                  <Divider orientation="vertical" height="40px" />
                  <VStack align="end" spacing={0}>
                    <Text fontSize="sm" color="gray.500">Health Score</Text>
                    <MotionBox variants={pulseVariant} initial="initial" animate="animate">
                      <Heading size="xl" color={`${color}.500`}>{asset.health_score}</Heading>
                    </MotionBox>
                  </VStack>
                </HStack>
                {trend !== 0 && (
                  <HStack spacing={2}>
                    <Icon as={trend > 0 ? FaArrowUp : FaArrowDown} color={trend > 0 ? 'green.500' : 'red.500'} />
                    <Text fontSize="sm" color={trend > 0 ? 'green.500' : 'red.500'} fontWeight="bold">
                      {Math.abs(trend).toFixed(1)} pts {trend > 0 ? 'improving' : 'declining'}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Flex>
          </Box>

          {/* ── AI + PREDICTION ─────────────────────────────────────────── */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box bg={aiCardBg} p={6} borderRadius="xl" borderWidth="1px" borderColor="blue.200" position="relative" overflow="hidden" shadow="md" _hover={{ shadow: 'xl', transform: 'translateY(-2px)', transition: 'all 0.3s ease' }}>
              <Icon as={FaRobot} position="absolute" right="-20px" bottom="-20px" w={32} h={32} color={aiIconColor} transform="rotate(-15deg)" />
              <VStack align="start" spacing={3} position="relative" zIndex={1}>
                <HStack color="blue.600">
                  <MotionBox variants={pulseVariant} initial="initial" animate="animate"><Icon as={FaBolt} /></MotionBox>
                  <Text fontWeight="bold" fontSize="sm" textTransform="uppercase">AI Recommendation</Text>
                </HStack>
                <Text fontSize="lg" fontWeight="medium" color={textColor}>"{asset.recommendation || 'No immediate actions recommended.'}"</Text>
                <Text fontSize="sm" color="gray.500">{aiDisplay.subtitle}</Text>
                <HStack mt={2}>
                  <Badge colorScheme={aiDisplay.statusBadgeScheme} variant="subtle"><Icon as={FaLightbulb} mr={1} />{aiDisplay.statusBadgeText}</Badge>
                  <Badge colorScheme={aiDisplay.confidenceBadgeScheme} variant="subtle">Confidence: {aiDisplay.confidenceScore === null ? '—' : `${aiDisplay.confidenceScore}%`}</Badge>
                </HStack>
              </VStack>
            </Box>

            <Flex direction="column" bg={predictionCardBg} p={6} borderRadius="xl" shadow="md" borderWidth="1px" borderColor={borderColor} justify="center" _hover={{ shadow: 'xl', transition: 'all 0.3s ease' }}>
              <HStack mb={4} justify="space-between">
                <Text fontWeight="bold">Failure Prediction</Text>
                {!prediction && <Icon as={FaHeartbeat} color="purple.400" w={5} h={5} />}
              </HStack>
              {!prediction ? (
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="sm" color="gray.500">Run the advanced ML model to estimate Remaining Useful Life (RUL) and predict potential failure points.</Text>
                  <Button rightIcon={<Icon as={FaArrowRight} />} onClick={handleGeneratePrediction} isLoading={predictionLoading} loadingText="Running Model..." colorScheme="purple" variant="solid" width="full" size="lg" shadow="md" _hover={{ shadow: 'lg' }}>Generate Prediction</Button>
                  <HStack fontSize="xs" color="gray.500" justify="center"><Icon as={FaClock} /><Text>Typically takes 2-3 seconds</Text></HStack>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between" align="center" p={3} bg={factorsBg} borderRadius="lg">
                    <VStack align="start" spacing={0}>
                      <Text color="gray.500" fontSize="xs">Estimated RUL</Text>
                      <HStack>
                        <Text fontWeight="bold" fontSize="3xl">{prediction.predicted_rul_days || prediction.days_to_failure || 'N/A'}</Text>
                        <Text fontSize="sm" color="gray.500">Days</Text>
                      </HStack>
                    </VStack>
                    <CircularProgress value={(prediction.confidence_score || prediction.confidence || 0) * 100} color="green.400" size="60px" thickness="8px">
                      <CircularProgressLabel fontSize="xs" fontWeight="bold">{((prediction.confidence_score || prediction.confidence || 0) * 100).toFixed(0)}%</CircularProgressLabel>
                    </CircularProgress>
                  </HStack>
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="xs" color="gray.500">Criticality Index</Text>
                      <Text fontSize="xs" fontWeight="bold">{Math.max(0, 100 - ((prediction.predicted_rul_days || prediction.days_to_failure || 0) / 365 * 100)).toFixed(0)}%</Text>
                    </HStack>
                    <Progress value={Math.max(0, 100 - ((prediction.predicted_rul_days || prediction.days_to_failure || 0) / 365 * 100))} colorScheme="purple" size="sm" borderRadius="full" hasStripe isAnimated />
                  </Box>
                  <HStack pt={2} spacing={2}>
                    <Button size="sm" leftIcon={<Icon as={FaDatabase} />} colorScheme="blue" variant="outline" flex="1" isLoading={savingPrediction} loadingText="Saving" onClick={handleSavePrediction}>Save Data</Button>
                    <Button size="sm" variant="ghost" flex="1" onClick={() => setPrediction(null)}>Reset</Button>
                  </HStack>
                </VStack>
              )}
            </Flex>
          </SimpleGrid>

          {/* ── CHARTS ──────────────────────────────────────────────────── */}
          <Box bg={cardHeaderBg} p={6} borderRadius="xl" shadow="md" borderWidth="1px" borderColor={borderColor}>
            <Tabs colorScheme="teal" variant="soft-rounded" index={activeMetricTab} onChange={setActiveMetricTab}>
              <Flex justify="space-between" align="center" mb={4}>
                <HStack>
                  <Box p={2} bg={`${color}.100`} borderRadius="md"><Icon as={FaChartLine} color={`${color}.600`} /></Box>
                  <Box><Heading size="sm">Performance Analytics</Heading><Text fontSize="xs" color="gray.500">Multi-dimensional asset insights</Text></Box>
                </HStack>
                <HStack>
                  <Tooltip label={showAnomalies ? 'Hide anomalies' : 'Show anomalies'} hasArrow>
                    <IconButton size="sm" icon={<Icon as={showAnomalies ? FaEyeSlash : FaEye} />} onClick={() => setShowAnomalies(!showAnomalies)} variant="ghost" colorScheme="red" />
                  </Tooltip>
                  {anomalies.length > 0 && <Badge colorScheme="red" fontSize="xs">{anomalies.length} Anomalies</Badge>}
                </HStack>
              </Flex>
              <TabList mb={4}>
                <Tab fontSize="xs"><Icon as={FaChartArea} mr={2} />Health Trend</Tab>
                <Tab fontSize="xs"><Icon as={FaChartBar}  mr={2} />Performance</Tab>
                <Tab fontSize="xs"><Icon as={FaChartPie}  mr={2} />Distribution</Tab>
              </TabList>
              <TabPanels>
                <TabPanel p={0}>
                  {loading ? (
                    <Flex justify="center" align="center" h="300px"><Spinner color="blue.500" thickness="3px" size="xl" /></Flex>
                  ) : history.length > 0 ? (
                    <ReactApexChart options={chartOptions} series={chartSeries} type="area" height={320} />
                  ) : (
                    <Flex justify="center" align="center" h="300px" bg={emptyChartBg} borderRadius="lg">
                      <VStack><Icon as={FaChartLine} boxSize={12} color="gray.400" /><Text color="gray.400">No historical data available</Text></VStack>
                    </Flex>
                  )}
                </TabPanel>
                <TabPanel p={0}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={4}>
                    <VStack align="stretch">
                      <Text fontSize="sm" fontWeight="bold">Performance Metrics</Text>
                      {[
                        { label: 'System Availability', value: perfData?.availability ? `${perfData.availability}%` : 'N/A', scheme: 'blue'   },
                        { label: 'Total Downtime',       value: perfData?.totalDowntimeHours ? `${perfData.totalDowntimeHours} hrs` : '0 hrs', scheme: 'orange' },
                        { label: 'MTBF',                 value: perfData?.mtbf_hours ? `${perfData.mtbf_hours} hrs` : 'Perfect', scheme: 'green'  },
                      ].map(({ label, value, scheme }) => (
                        <HStack key={label} justify="space-between" p={3} bg={factorsBg} borderRadius="md">
                          <Text fontSize="xs">{label}</Text>
                          {loading ? <Spinner size="xs" /> : <Badge colorScheme={scheme}>{value}</Badge>}
                        </HStack>
                      ))}
                    </VStack>
                    <VStack align="stretch">
                      <Text fontSize="sm" fontWeight="bold">Efficiency Index (OEE)</Text>
                      <Flex justify="center" align="center" p={4} bg={factorsBg} borderRadius="md" h="full">
                        {loading ? <Spinner /> : (
                          <CircularProgress value={perfData?.oee || 0} color="teal.400" size="120px" thickness="12px">
                            <CircularProgressLabel fontSize="2xl" fontWeight="bold">{perfData?.oee || 0}%</CircularProgressLabel>
                          </CircularProgress>
                        )}
                      </Flex>
                    </VStack>
                  </SimpleGrid>
                </TabPanel>
                <TabPanel p={0}>
                  <Text fontSize="sm" color="gray.500" mb={4}>Asset health score distribution over time</Text>
                  <SimpleGrid columns={3} spacing={4}>
                    <Box p={4} bg="green.50"  borderRadius="lg" borderLeftWidth="4px" borderLeftColor="green.400"><Text fontSize="xs" color="gray.600">Healthy (75-100)</Text><Text fontSize="2xl" fontWeight="bold" color="green.600">{distributionStats.healthy}</Text></Box>
                    <Box p={4} bg="orange.50" borderRadius="lg" borderLeftWidth="4px" borderLeftColor="orange.400"><Text fontSize="xs" color="gray.600">Warning (50-74)</Text><Text fontSize="2xl" fontWeight="bold" color="orange.600">{distributionStats.warning}</Text></Box>
                    <Box p={4} bg="red.50"    borderRadius="lg" borderLeftWidth="4px" borderLeftColor="red.400"><Text fontSize="xs" color="gray.600">Critical (&lt;50)</Text><Text fontSize="2xl" fontWeight="bold" color="red.600">{distributionStats.critical}</Text></Box>
                  </SimpleGrid>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>

          {/* ── ANOMALIES ───────────────────────────────────────────────── */}
          {showAnomalies && anomalies.length > 0 && (
            <MotionBox initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} bg={anomalyBg} p={6} borderRadius="xl" borderWidth="2px" borderColor="red.300">
              <HStack mb={4}><Icon as={FaExclamationCircle} color="red.500" boxSize={6} /><Heading size="sm" color="red.700">Detected Anomalies</Heading><Badge colorScheme="red">{anomalies.length}</Badge></HStack>
              <VStack align="stretch" spacing={3}>
                {anomalies.map((anomaly, idx) => (
                  <Flex key={idx} justify="space-between" align="center" p={3} bg={anomalyItemBg} borderRadius="md" borderLeftWidth="3px" borderLeftColor="red.500">
                    <VStack align="start" spacing={0}><Text fontSize="sm" fontWeight="bold">{new Date(anomaly.recorded_at).toLocaleDateString()}</Text><Text fontSize="xs" color="gray.500">Deviation: {anomaly.deviation}σ from mean</Text></VStack>
                    <Badge colorScheme="red" fontSize="md" px={3} py={1}>Score: {anomaly.health_score}</Badge>
                  </Flex>
                ))}
              </VStack>
            </MotionBox>
          )}

          {/* ── CONTRIBUTING FACTORS ────────────────────────────────────── */}
          <Box>
            <Heading size="sm" mb={3}><Icon as={FaInfoCircle} mr={2} color="blue.500" />Contributing Factors</Heading>
            <Accordion allowMultiple>
              <AccordionItem border="none" bg={factorsBg} borderRadius="lg" mb={2}>
                <h2><AccordionButton _expanded={{ bg: accordionExpandedBg }}><Box flex="1" textAlign="left" fontWeight="bold"><Icon as={FaWrench} mr={2} />Primary Factors</Box><AccordionIcon /></AccordionButton></h2>
                <AccordionPanel pb={4}><Text fontSize="sm" color="gray.600">{asset.reasons || 'No specific negative factors identified at this time.'}</Text></AccordionPanel>
              </AccordionItem>
              <AccordionItem border="none" bg={factorsBg} borderRadius="lg">
                <h2><AccordionButton><Box flex="1" textAlign="left" fontWeight="bold"><Icon as={FaHistory} mr={2} />Historical Context</Box><AccordionIcon /></AccordionButton></h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between"><Text fontSize="sm">Previous Failures:</Text>{loading ? <Spinner size="xs" /> : <Badge>{perfData?.failureCount || 0} incidents</Badge>}</HStack>
                    <HStack justify="space-between"><Text fontSize="sm">Average Recovery Time:</Text>{loading ? <Spinner size="xs" /> : <Badge colorScheme="orange">{perfData?.mttr_hours ? `${perfData.mttr_hours} hours` : '0 hours'}</Badge>}</HStack>
                    <HStack justify="space-between"><Text fontSize="sm">Last Major Service:</Text><Badge colorScheme="green">{asset.last_maintenance_date ? new Date(asset.last_maintenance_date).toLocaleDateString() : 'Unknown'}</Badge></HStack>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

        </VStack>
      </MotionBox>
    </AnimatePresence>
  );
});
AssetDetailView.displayName = 'AssetDetailView';

// ============================================================================
// SETTINGS DRAWER  (🔒 CORE — UNCHANGED FROM v2.3)
// ============================================================================

const SettingsDrawer = React.memo(({ isOpen, onClose, settings, onSettingsChange }) => (
  <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
    <DrawerOverlay backdropFilter="blur(4px)" />
    <DrawerContent>
      <DrawerCloseButton />
      <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={FaCog} /><Text>Dashboard Settings</Text></HStack></DrawerHeader>
      <DrawerBody>
        <VStack align="stretch" spacing={6} pt={4}>
          <Box>
            <Text fontWeight="bold" mb={3}>Display Options</Text>
            <VStack align="stretch" spacing={3}>
              <FormControl display="flex" alignItems="center"><FormLabel htmlFor="auto-refresh" mb="0" flex="1">Auto-refresh data</FormLabel><Switch id="auto-refresh" isChecked={settings.autoRefresh} onChange={e => onSettingsChange('autoRefresh', e.target.checked)} /></FormControl>
              <FormControl display="flex" alignItems="center"><FormLabel htmlFor="show-trends" mb="0" flex="1">Show trend indicators</FormLabel><Switch id="show-trends" isChecked={settings.showTrends} onChange={e => onSettingsChange('showTrends', e.target.checked)} /></FormControl>
              <FormControl display="flex" alignItems="center"><FormLabel htmlFor="show-notifications" mb="0" flex="1">Enable notifications</FormLabel><Switch id="show-notifications" isChecked={settings.notifications} onChange={e => onSettingsChange('notifications', e.target.checked)} /></FormControl>
            </VStack>
          </Box>
          <Divider />
          <Box>
            <Text fontWeight="bold" mb={3}>Alert Thresholds</Text>
            <VStack align="stretch" spacing={4}>
              <Box>
                <HStack justify="space-between" mb={2}><Text fontSize="sm">Critical Alert Threshold</Text><Badge colorScheme="red">{settings.criticalThreshold}</Badge></HStack>
                <Slider value={settings.criticalThreshold} min={0} max={100} step={5} onChange={val => onSettingsChange('criticalThreshold', val)} colorScheme="red"><SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb /></Slider>
              </Box>
              <Box>
                <HStack justify="space-between" mb={2}><Text fontSize="sm">Warning Alert Threshold</Text><Badge colorScheme="orange">{settings.warningThreshold}</Badge></HStack>
                <Slider value={settings.warningThreshold} min={0} max={100} step={5} onChange={val => onSettingsChange('warningThreshold', val)} colorScheme="orange"><SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb /></Slider>
              </Box>
            </VStack>
          </Box>
          <Divider />
          <Box>
            <Text fontWeight="bold" mb={3}>Data Settings</Text>
            <FormControl>
              <FormLabel fontSize="sm">History Window (days)</FormLabel>
              <Select value={settings.historyDays} onChange={e => onSettingsChange('historyDays', parseInt(e.target.value))}>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
              </Select>
            </FormControl>
          </Box>
        </VStack>
      </DrawerBody>
    </DrawerContent>
  </Drawer>
));
SettingsDrawer.displayName = 'SettingsDrawer';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PredictiveHealthDashboard = ({ liveVersion = 0, lastLiveEvent = null, onAskAI = null }) => {

  // ── State ────────────────────────────────────────────────────────────────
  const [assets,          setAssets]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [selectedAsset,   setSelectedAsset]   = useState(null);
  const [isCalculating,   setIsCalculating]   = useState(false);
  const [searchTerm,      setSearchTerm]      = useState('');
  const [filterStatus,    setFilterStatus]    = useState('all');
  const [sortBy,          setSortBy]          = useState('health_score');
  const [sortOrder,       setSortOrder]       = useState('asc');
  const [currentPage,     setCurrentPage]     = useState(1);
  const [itemsPerPage,    setItemsPerPage]    = useState(25);
  const [viewMode,        setViewMode]        = useState('card');
  const [comparisonMode,  setComparisonMode]  = useState(false);
  const [comparedAssets,  setComparedAssets]  = useState([]);
  const [groupBy,         setGroupBy]         = useState('none');
  const [selectedRows,    setSelectedRows]    = useState(new Set());   // 🚀 bulk select
  const [wsStatus,        setWsStatus]        = useState(() => wsStatusOf(webSocketService?.status || (webSocketService?.isConnected ? 'connected' : 'connecting'))); // --- UPDATE: real status
  const [liveUpdates,     setLiveUpdates]     = useState({ count: 0, at: null });   // --- UPDATE
  const [flashIds,        setFlashIds]        = useState(() => new Set());          // --- UPDATE
  const [isAnalyzing,     setIsAnalyzing]     = useState(false);                    // --- UPDATE
  const [alertDismissed,  setAlertDismissed]  = useState(false);      // 🚀 banner dismiss
  const [jumpPage,        setJumpPage]        = useState('');          // 🚀 jump-to-page
  const [settings,        setSettings]        = useState({
    autoRefresh: false, showTrends: true, notifications: true,
    criticalThreshold: 50, warningThreshold: 75, historyDays: 90,
  });

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const { isOpen: isExportOpen,   onOpen: onExportOpen,   onClose: onExportClose   } = useDisclosure();
  const toast = useToast();

  // ── Theme ────────────────────────────────────────────────────────────────
  const bg              = useColorModeValue('gray.50', 'gray.900');
  const cardBg          = useColorModeValue('white', 'gray.800');
  const statCardBg      = useColorModeValue('white', 'gray.800');
  const tableHoverBg    = useColorModeValue('gray.50', 'gray.700');
  const tableSelectedBg = useColorModeValue('blue.50', 'blue.900');
  const tableHeaderBg   = useColorModeValue('gray.50', 'gray.700');
  const mutedColor      = useColorModeValue('gray.500', 'gray.400');
  const bulkBarBg       = useColorModeValue('blue.50',  'blue.900');   // 🚀 bulk action bar
  const aiStripBg       = useColorModeValue('purple.50', 'whiteAlpha.100'); // --- UPDATE
  const { data: mlOverview } = useMlOverview();                             // --- UPDATE (live via ml_events)
  const canRunAnalysis = useMemo(() => String(currentUser()?.role || '').toLowerCase() === 'admin', []); // POST /predictive-failure/run-analysis is admin-only
  const rulModel = useMemo(() => (mlOverview?.models || []).find(m => m.key === 'RUL_PREDICTION') || null, [mlOverview]);

  // ── Refs ─────────────────────────────────────────────────────────────────
  const refreshInterval      = useRef(null);
  const isMounted            = useRef(true);
  const hasInitiallySelected = useRef(false);
  const listContainerRef     = useRef(null);   // 🚀 for keyboard scroll-into-view

  // ── Debounced search ─────────────────────────────────────────────────────
  const debouncedSearch = useDebounce(searchTerm, 300);  // 🚀 gates the filter useMemo

  // ── 🔒 CORE: fetchData (UNCHANGED) ───────────────────────────────────────
  const fetchData = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (isMounted.current) { if (!quiet) setLoading(true); setError(null); }
      const response = await analyticsService.getLatestAssetHealthScores();
      if (!isMounted.current) return;
      if (response.success) {
        const sortedAssets = response.data.sort((a, b) => a.health_score - b.health_score);
        setAssets(sortedAssets);
        if (sortedAssets.length > 0 && !hasInitiallySelected.current) {
          setSelectedAsset(sortedAssets[0]);
          hasInitiallySelected.current = true;
        }
        if (!quiet) setAlertDismissed(false); // 🚀 re-show banner on fresh data (not on live refetches)
      } else {
        throw new Error(response.message || 'Failed to fetch data.');
      }
    } catch (err) {
      if (isMounted.current && !quiet) setError('Failed to load asset health data.');
      console.error(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, [fetchData]);

  // ── --- UPDATE (round 3): live events + real websocket status ──────────────
  const quietTimer = useRef(null);
  const scheduleQuietRefetch = useCallback(() => {
    if (quietTimer.current) return;
    quietTimer.current = setTimeout(() => {
      quietTimer.current = null;
      if (isMounted.current) fetchData({ quiet: true });
    }, 2500);
  }, [fetchData]);

  const applyLiveEvent = useCallback((type, raw) => {
    if (!isMounted.current) return;
    const payload = unwrapFrame(raw) || {};
    const id = payload.asset_id ?? payload.assetId;
    if (type === 'asset_health_events' && id !== undefined) {
      const patch = (a) => ((String(a.asset_id || a.id) === String(id)) ? { ...a, health_score: payload.health_score ?? a.health_score, updated_at: payload.timestamp || a.updated_at } : a);
      setAssets(prev => prev.map(patch));
      setSelectedAsset(prev => (prev ? patch(prev) : prev));
    } else if (PATCH_EVENTS.has(type) && id !== undefined) {
      const patch = (a) => ((String(a.asset_id || a.id) === String(id)) ? {
        ...a,
        ai_rul_days: payload.predicted_rul_days ?? (payload.predicted_rul_years != null ? Math.round(payload.predicted_rul_years * 365) : a.ai_rul_days),
        ai_risk_score: payload.risk_score ?? a.ai_risk_score,
        ai_health_status: payload.health_status ?? a.ai_health_status,
        ai_updated_at: payload.timestamp || payload.at || new Date().toISOString(),
      } : a);
      setAssets(prev => prev.map(patch));
      setSelectedAsset(prev => (prev ? patch(prev) : prev));
      setFlashIds(prev => new Set(prev).add(String(id)));
      setTimeout(() => { if (isMounted.current) setFlashIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; }); }, 4000);
    } else if (REFETCH_EVENTS.has(type)) {
      scheduleQuietRefetch();
    } else {
      return;
    }
    setLiveUpdates(u => ({ count: u.count + 1, at: new Date() }));
  }, [scheduleQuietRefetch]);

  useEffect(() => {
    const types = ['asset_health_events', ...PATCH_EVENTS, ...REFETCH_EVENTS];
    const handlers = types.map(t => [t, (msg) => applyLiveEvent(t, msg)]);
    const onStatus = (evt) => { if (isMounted.current && evt?.status) setWsStatus(wsStatusOf(evt.status)); };
    try {
      handlers.forEach(([t, h]) => webSocketService?.subscribe?.(t, h));
      webSocketService?.subscribe?.('status', onStatus);
      if (webSocketService && !webSocketService.isConnected && !webSocketService.isConnecting) webSocketService.connect?.();
    } catch {
      setWsStatus(WS_STATUS.DISCONNECTED);
    }
    return () => {
      handlers.forEach(([t, h]) => webSocketService?.unsubscribe?.(t, h));
      webSocketService?.unsubscribe?.('status', onStatus);
      if (quietTimer.current) clearTimeout(quietTimer.current);
    };
  }, [applyLiveEvent]);

  // events forwarded by the Analytics Hub (covers pages where this component's own socket is shared)
  const lastHubVersion = useRef(liveVersion);
  useEffect(() => {
    if (liveVersion === lastHubVersion.current) return;
    lastHubVersion.current = liveVersion;
    if (lastLiveEvent?.type && (PATCH_EVENTS.has(lastLiveEvent.type) || REFETCH_EVENTS.has(lastLiveEvent.type))) scheduleQuietRefetch();
  }, [liveVersion, lastLiveEvent, scheduleQuietRefetch]);

  const handleRunAnalysis = useCallback(async () => {
    setIsAnalyzing(true);
    try {
      const res = await runPredictiveAnalysis();
      const count = res?.count ?? (Array.isArray(res?.data) ? res.data.length : 0);
      const critical = res?.byStatus?.critical ?? 0;
      toast({ title: 'AI analysis complete', description: `${count} assets scored by the RUL model${critical ? `, ${critical} critical` : ''}.`, status: critical ? 'warning' : 'success', duration: 5000, isClosable: true });
      fetchData({ quiet: true });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Analysis failed';
      toast({ title: /already running/i.test(msg) ? 'Analysis already running' : 'Analysis failed', description: msg, status: /already running/i.test(msg) ? 'info' : 'error', duration: 6000, isClosable: true });
    } finally {
      if (isMounted.current) setIsAnalyzing(false);
    }
  }, [fetchData, toast]);
  // --- END UPDATE ──────────────────────────────────────────────────────────

  // ── Auto-refresh (🔒 CORE — UNCHANGED) ───────────────────────────────────
  useEffect(() => {
    if (settings.autoRefresh) {
      refreshInterval.current = setInterval(() => {
        fetchData();
        if (settings.notifications) {
          toast({ title: 'Data Refreshed', description: 'Asset health scores updated automatically', status: 'info', duration: 2000, position: 'bottom-right', isClosable: true });
        }
      }, 60_000);
    } else {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    }
    return () => { if (refreshInterval.current) clearInterval(refreshInterval.current); };
  }, [settings.autoRefresh, settings.notifications, fetchData, toast]);

  // ── 🚀 Keyboard navigation on asset list ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedAsset(prev => {
          if (!prev || paginatedAssets.length === 0) return prev;
          const idx = paginatedAssets.findIndex(a => (a.asset_id || a.id) === (prev.asset_id || prev.id));
          const next = e.key === 'ArrowDown'
            ? paginatedAssets[Math.min(idx + 1, paginatedAssets.length - 1)]
            : paginatedAssets[Math.max(idx - 1, 0)];
          return next || prev;
        });
      }
      if (e.key === 'Escape') setSelectedAsset(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }); // intentionally no dep array — needs fresh paginatedAssets

  // ── 🔒 CORE: Filter + sort (now gated by debouncedSearch) ────────────────
  const filteredAssets = useMemo(() => {
    let filtered = [...assets];

    if (debouncedSearch) {
      filtered = filtered.filter(a =>
        a.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        a.type.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (a.location && a.location.toLowerCase().includes(debouncedSearch.toLowerCase()))
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(a => {
        if (filterStatus === 'critical') return a.health_score < settings.criticalThreshold;
        if (filterStatus === 'warning')  return a.health_score >= settings.criticalThreshold && a.health_score < settings.warningThreshold;
        if (filterStatus === 'healthy')  return a.health_score >= settings.warningThreshold;
        return true;
      });
    }

    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'health_score') cmp = a.health_score - b.health_score;
      if (sortBy === 'name')         cmp = a.name.localeCompare(b.name);
      if (sortBy === 'type')         cmp = a.type.localeCompare(b.type);
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return filtered;
  }, [assets, debouncedSearch, filterStatus, sortBy, sortOrder, settings]);

  // Reset page when filter changes
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, filterStatus, sortBy, sortOrder]);

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages      = useMemo(() => Math.ceil(filteredAssets.length / itemsPerPage), [filteredAssets.length, itemsPerPage]);
  const paginatedAssets = useMemo(() => filteredAssets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage), [filteredAssets, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  }, [totalPages]);

  // ── 🔒 CORE: Sort handler (UNCHANGED) ────────────────────────────────────
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }, [sortBy]);

  // ── Grouping ──────────────────────────────────────────────────────────
  const groupedAssets = useMemo(() => {
    if (groupBy === 'none') return { 'All Assets': paginatedAssets };
    return paginatedAssets.reduce((groups, asset) => {
      let key;
      if (groupBy === 'type')     key = asset.type || 'Unknown';
      if (groupBy === 'status')   key = getAssetStatus(asset.health_score).label;
      if (groupBy === 'location') key = asset.location || 'Unknown Location';
      if (!groups[key]) groups[key] = [];
      groups[key].push(asset);
      return groups;
    }, {});
  }, [paginatedAssets, groupBy]);

  // ── Summary stats (isolated memo) ────────────────────────────────────
  const summaryStats = useMemo(() => {
    const critical = assets.filter(a => a.health_score < settings.criticalThreshold).length;
    const warning  = assets.filter(a => a.health_score >= settings.criticalThreshold && a.health_score < settings.warningThreshold).length;
    const healthy  = assets.filter(a => a.health_score >= settings.warningThreshold).length;
    const avgScore = assets.length > 0 ? (assets.reduce((s, a) => s + a.health_score, 0) / assets.length).toFixed(1) : 0;
    return { critical, warning, healthy, avgScore, total: assets.length };
  }, [assets, settings]);

  // ── 🚀 Bulk selection handlers ────────────────────────────────────────
  const handleToggleRow = useCallback((id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.size === paginatedAssets.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedAssets.map(a => a.asset_id || a.id)));
    }
  }, [paginatedAssets, selectedRows.size]);

  const handleExportSelected = useCallback(() => {
    const data = assets.filter(a => selectedRows.has(a.asset_id || a.id));
    const ts   = new Date().toISOString().split('T')[0];
    exportToCSV(data, `selected-assets-${ts}.csv`);
    toast({ title: `${data.length} assets exported`, status: 'success', duration: 2000, isClosable: true });
    setSelectedRows(new Set());
  }, [assets, selectedRows, toast]);

  // ── 🔒 CORE handlers ─────────────────────────────────────────────────
  const handleManualCalculation = async () => {
    setIsCalculating(true);
    toast({ title: 'Calculation Initiated', description: 'Updating health models for all assets...', status: 'info', duration: 2000 });
    try {
      const response = await analyticsService.triggerHealthScoreCalculation();
      if (response.success) {
        toast({ title: 'Update Complete', description: 'Asset health scores are now current.', status: 'success', duration: 3000 });
        await fetchData();
      } else throw new Error(response.message);
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error' });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSettingsChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleExport = useCallback((format) => {
    const ts = new Date().toISOString().split('T')[0];
    if (format === 'csv') exportToCSV(filteredAssets, `asset-health-${ts}.csv`);
    else                  exportToJSON(filteredAssets, `asset-health-${ts}.json`);
    toast({ title: 'Export Complete', description: `${filteredAssets.length} assets exported as ${format.toUpperCase()}`, status: 'success', duration: 3000 });
    onExportClose();
  }, [filteredAssets, toast, onExportClose]);

  // ── Loading / Error states ────────────────────────────────────────────
  if (loading && assets.length === 0) {
    return (
      <Flex justify="center" align="center" h="100vh" bg={bg}>
        <VStack spacing={4} w="full" maxW="md">
          <MotionBox initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          </MotionBox>
          <Skeleton height="40px" w="100%" borderRadius="md" />
          <Skeleton height="20px" w="70%" borderRadius="md" />
          <VStack w="full" spacing={4} mt={8}>
            <Skeleton height="100px" w="100%" borderRadius="xl" />
            <Skeleton height="100px" w="100%" borderRadius="xl" />
            <Skeleton height="100px" w="100%" borderRadius="xl" />
          </VStack>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Container maxW="container.md" pt={20}>
        <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px" borderRadius="lg">
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">Connection Failed</AlertTitle>
            <AlertDescription maxWidth="sm">{error}</AlertDescription>
            <Button mt={4} colorScheme="red" onClick={() => fetchData()}>Retry</Button>
          </Alert>
        </MotionBox>
      </Container>
    );
  }

  // ── MAIN RENDER ──────────────────────────────────────────────────────
  return (
    <Box bg={bg} minH="100vh" p={{ base: 4, md: 8 }}>
      <Container maxW="container.xl" p={0}>

        {/* ── 🚀 CRITICAL ALERT BANNER ──────────────────────────────────── */}
        {!alertDismissed && (
          <CriticalAlertBanner
            count={summaryStats.critical}
            onNavigate={() => { setFilterStatus('critical'); setAlertDismissed(true); }}
            onDismiss={() => setAlertDismissed(true)}
          />
        )}

        {/* ── TOP HEADER ─────────────────────────────────────────────────── */}
        <VStack spacing={6} align="stretch" mb={8}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Box>
              <MotionBox initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
                <HStack mb={1} spacing={3}>
                  <Heading size="lg" bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
                    Predictive Maintenance
                  </Heading>
                  {/* 🚀 Live WebSocket badge */}
                  <LiveConnectionBadge status={wsStatus} />
                </HStack>
                <Text color={mutedColor}>Real-time asset health monitoring &amp; failure prediction</Text>
                {liveUpdates.count > 0 && (
                  <Text fontSize="xs" color="purple.400" data-testid="live-updates">
                    {liveUpdates.count} live update{liveUpdates.count === 1 ? '' : 's'} · last {timeAgo(liveUpdates.at)}
                  </Text>
                )}
              </MotionBox>
            </Box>
            <HStack spacing={3} flexWrap="wrap">
              {onAskAI && (
                <Button variant="outline" colorScheme="purple" leftIcon={<Icon as={FaRobot} />}
                  onClick={() => onAskAI(`${summaryStats.critical} assets are critical and ${summaryStats.warning} show warnings. Which should we fix first and why?`)}>
                  Ask AI
                </Button>
              )}
              <Tooltip label="Export Data" hasArrow>
                <IconButton icon={<Icon as={FaFileExport} />} onClick={onExportOpen} variant="outline" colorScheme="purple" aria-label="Export" />
              </Tooltip>
              <Tooltip label="Settings" hasArrow>
                <IconButton icon={<Icon as={FaCog} />} onClick={onSettingsOpen} variant="outline" colorScheme="gray" aria-label="Settings" />
              </Tooltip>
              <Button
                leftIcon={<Icon as={FaSync} className={isCalculating ? 'fa-spin' : ''} />}
                onClick={handleManualCalculation}
                isLoading={isCalculating}
                loadingText="Updating..."
                colorScheme="blue"
                size="md"
                shadow="md"
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                Refresh Models
              </Button>
            </HStack>
          </Flex>

          {/* --- UPDATE (round 3): AI strip --- */}
          <Flex bg={aiStripBg} borderRadius="lg" p={3} align="center" gap={3} wrap="wrap" data-testid="predictive-ai-strip">
            <Icon as={FaRobot} color="purple.400" />
            <Text fontSize="sm" fontWeight="600">Remaining Useful Life model</Text>
            {rulModel?.activeJob ? <Badge colorScheme="blue">training now</Badge>
              : rulModel?.champion ? <Badge colorScheme="green">v{rulModel.champion.model_version}</Badge>
                : <Badge colorScheme={mlOverview ? 'orange' : 'gray'}>{mlOverview ? 'no production version' : 'loading…'}</Badge>}
            {rulModel?.champion && (
              <Text fontSize="xs" color={mutedColor}>
                {rulModel.metric?.label}: {fmtMetric(rulModel.champion.metric, rulModel.metric)} · trained {timeAgo(rulModel.champion.last_trained_at || rulModel.champion.created_at)}
              </Text>
            )}
            {mlOverview && !mlOverview.summary?.mlOnline && <Badge colorScheme="red">ML engine offline</Badge>}
            <ExplainButton topic="RUL_PREDICTION" />
            {canRunAnalysis && (
              <Button ml="auto" size="sm" colorScheme="purple" leftIcon={<Icon as={FaBolt} />} onClick={handleRunAnalysis} isLoading={isAnalyzing} loadingText="Scoring assets…" data-guide="run-analysis">
                Run AI analysis
              </Button>
            )}
          </Flex>
          {/* --- END UPDATE --- */}

          {/* KPI stat cards */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            {[
              { label: 'System Average Health', value: summaryStats.avgScore,  color: 'blue',   helpIcon: FaDatabase,            helpText: `${summaryStats.total} active assets`  },
              { label: 'Critical Attention',    value: summaryStats.critical,  color: 'red',    helpIcon: FaFire,                helpText: 'Requiring immediate action'           },
              { label: 'Warnings',              value: summaryStats.warning,   color: 'orange', helpIcon: FaExclamationTriangle, helpText: 'Showing degradation'                  },
              { label: 'Healthy Assets',        value: summaryStats.healthy,   color: 'green',  helpIcon: FaCheckCircle,         helpText: 'Optimal performance'                  },
            ].map(({ label, value, color, helpIcon, helpText }) => (
              <MotionBox key={label} whileHover={{ y: -4, shadow: 'xl' }} transition={{ duration: 0.2 }}>
                <Stat bg={statCardBg} p={5} borderRadius="xl" shadow="md" borderLeft="4px solid" borderColor={`${color}.400`} position="relative" overflow="hidden">
                  <StatLabel color="gray.500" fontSize="sm">{label}</StatLabel>
                  <StatNumber fontSize="3xl" color={`${color}.600`} fontWeight="800">{value}</StatNumber>
                  <StatHelpText><Icon as={helpIcon} mr={1} />{helpText}</StatHelpText>
                </Stat>
              </MotionBox>
            ))}
          </SimpleGrid>

          {/* 🚀 Health distribution bar — sits below KPI row for instant fleet overview */}
          <HealthDistributionBar
            healthy={summaryStats.healthy}
            warning={summaryStats.warning}
            critical={summaryStats.critical}
            total={summaryStats.total}
          />
        </VStack>

        {/* ── 🚀 BULK ACTION BAR (slides in when rows are checked) ─────────── */}
        <AnimatePresence>
          {selectedRows.size > 0 && (
            <MotionBox
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.2 }}
              bg={bulkBarBg}
              borderWidth="1px"
              borderColor="blue.300"
              borderRadius="xl"
              px={5}
              py={3}
              mb={4}
            >
              <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                <HStack spacing={3}>
                  <Icon as={FaCheckSquare} color="blue.500" boxSize={4} />
                  <Text fontSize="sm" fontWeight="bold" color="blue.700">
                    {selectedRows.size} asset{selectedRows.size > 1 ? 's' : ''} selected
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  <Button size="xs" colorScheme="blue" leftIcon={<Icon as={FaFileExport} />} onClick={handleExportSelected}>
                    Export Selected (CSV)
                  </Button>
                  <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => setSelectedRows(new Set())}>
                    Clear Selection
                  </Button>
                </HStack>
              </Flex>
            </MotionBox>
          )}
        </AnimatePresence>

        {/* ── MAIN CONTENT GRID ──────────────────────────────────────────── */}
        {/* Updated from SimpleGrid+colSpan to Grid templateColumns (v2.3 layout fix preserved) */}
        <Grid
          templateColumns={{ base: '1fr', lg: 'minmax(300px, 380px) 1fr' }}
          gap={6}
          h={{ base: 'auto', lg: 'calc(100vh - 320px)' }}
          minH="600px"
        >
          {/* ── LEFT PANEL: ASSET LIST ─────────────────────────────────── */}
          <GridItem display="flex" flexDirection="column" minH={0}>

            {/* Sticky search + controls header */}
            <MotionBox bg={cardBg} p={4} borderRadius="xl" shadow="md" mb={4} flexShrink={0} initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
              <VStack spacing={3}>
                {/* Search */}
                <InputGroup>
                  <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
                  <Input
                    placeholder="Search assets by name, type, or location..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    variant="filled"
                    _focus={{ borderColor: 'blue.400' }}
                  />
                </InputGroup>

                {/* 🚀 Status filter as toggle buttons with live counts */}
                <StatusFilterButtons
                  value={filterStatus}
                  onChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}
                  counts={summaryStats}
                />

                <HStack w="full" spacing={2}>
                  {/* Sort menu */}
                  <Menu>
                    <MenuButton as={IconButton} icon={<Icon as={sortOrder === 'asc' ? FaSortAmountUp : FaSortAmountDown} />} size="sm" variant="outline" />
                    <MenuList>
                      <MenuItem onClick={() => handleSort('health_score')}><Icon as={FaTachometerAlt} mr={2} />Sort by Health</MenuItem>
                      <MenuItem onClick={() => handleSort('name')}><Icon as={FaSortAmountDown} mr={2} />Sort by Name</MenuItem>
                      <MenuItem onClick={() => handleSort('type')}><Icon as={FaLayerGroup} mr={2} />Sort by Type</MenuItem>
                      <MenuDivider />
                      <MenuItem onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}><Icon as={sortOrder === 'asc' ? FaSortAmountDown : FaSortAmountUp} mr={2} />{sortOrder === 'asc' ? 'Switch to Descending' : 'Switch to Ascending'}</MenuItem>
                    </MenuList>
                  </Menu>

                  {/* Grouping */}
                  <Select value={groupBy} onChange={e => setGroupBy(e.target.value)} variant="filled" size="sm" flex="1">
                    <option value="none">No Grouping</option>
                    <option value="type">Group by Type</option>
                    <option value="status">Group by Status</option>
                    <option value="location">Group by Location</option>
                  </Select>

                  {/* View mode */}
                  <ButtonGroup size="sm" isAttached variant="outline">
                    <IconButton icon={<Icon as={FaThLarge} />} onClick={() => setViewMode('card')}  isActive={viewMode === 'card'}  aria-label="Card view"  />
                    <IconButton icon={<Icon as={FaList}   />} onClick={() => setViewMode('list')}  isActive={viewMode === 'list'}  aria-label="List view"  />
                    <IconButton icon={<Icon as={FaTable}  />} onClick={() => setViewMode('table')} isActive={viewMode === 'table'} aria-label="Table view" />
                  </ButtonGroup>
                </HStack>

                <Flex w="full" justify="space-between" align="center">
                  <HStack fontSize="xs" color={mutedColor}>
                    <Icon as={FaFilter} />
                    <Text>{filteredAssets.length} of {assets.length} assets</Text>
                    {/* 🚀 keyboard hint */}
                    <Tooltip label="Use ↑ ↓ arrow keys to navigate, Esc to deselect" hasArrow>
                      <HStack spacing={1} cursor="default">
                        <Icon as={FaKeyboard} />
                        <Text>Keyboard nav</Text>
                      </HStack>
                    </Tooltip>
                  </HStack>
                  <HStack>
                    <Switch size="sm" isChecked={comparisonMode} onChange={e => setComparisonMode(e.target.checked)} />
                    <Text fontSize="xs">Compare</Text>
                  </HStack>
                </Flex>
              </VStack>
            </MotionBox>

            {/* Scrollable asset list */}
            <Box ref={listContainerRef} flex="1" minH={0} overflowY="auto" className="custom-scrollbar" pr={1} data-guide="predictions">
              {viewMode === 'table' ? (
                <TableContainer bg={cardBg} borderRadius="lg" shadow="sm">
                  <Table size="sm" variant="simple">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        {/* 🚀 Bulk-select column header */}
                        <Th w="36px">
                          <Checkbox
                            isChecked={selectedRows.size === paginatedAssets.length && paginatedAssets.length > 0}
                            isIndeterminate={selectedRows.size > 0 && selectedRows.size < paginatedAssets.length}
                            onChange={handleSelectAll}
                            colorScheme="blue"
                            size="sm"
                          />
                        </Th>
                        <SortableTh label="Asset"  field="name"         sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                        <SortableTh label="Type"   field="type"         sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                        <SortableTh label="Health" field="health_score" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} isNumeric />
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedAssets.map(asset => {
                        const { color, label } = getAssetStatus(asset.health_score);
                        const id               = asset.asset_id || asset.id;
                        const isChecked        = selectedRows.has(id);
                        return (
                          <Tr
                            key={id}
                            onClick={() => setSelectedAsset(asset)}
                            cursor="pointer"
                            _hover={{ bg: tableHoverBg }}
                            bg={selectedAsset?.asset_id === asset.asset_id ? tableSelectedBg : 'transparent'}
                          >
                            <Td onClick={e => { e.stopPropagation(); handleToggleRow(id); }}>
                              <Checkbox isChecked={isChecked} colorScheme="blue" size="sm" onChange={() => {}} />
                            </Td>
                            <Td fontWeight="medium" noOfLines={1} maxW="120px">{asset.name}</Td>
                            <Td><Badge colorScheme="purple" fontSize="xs">{asset.type}</Badge></Td>
                            <Td isNumeric>
                              {/* 🚀 progress bar in table cell */}
                              <VStack spacing={0.5} align="end">
                                <Badge colorScheme={color} fontSize="sm" px={2}>{asset.health_score}</Badge>
                                <Progress value={asset.health_score} colorScheme={color} size="xs" w="50px" borderRadius="full" bg="gray.100" />
                              </VStack>
                            </Td>
                            <Td><Badge colorScheme={color} variant="subtle">{label}</Badge></Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : Object.entries(groupedAssets).length > 0 ? (
                <VStack align="stretch" spacing={4} pb={4}>
                  {Object.entries(groupedAssets).map(([groupName, groupAssets]) => (
                    <Box key={groupName}>
                      {groupBy !== 'none' && (
                        <HStack mb={2} px={2}>
                          <Icon as={FaLayerGroup} color="gray.500" boxSize={3} />
                          <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase">{groupName}</Text>
                          <Badge size="sm" colorScheme="gray">{groupAssets.length}</Badge>
                        </HStack>
                      )}
                      <motion.div variants={listVariants} initial="hidden" animate="visible">
                        <VStack spacing={2} align="stretch">
                          <AnimatePresence mode="popLayout">
                            {groupAssets.map(asset => (
                              <AssetHealthCard
                                key={asset.asset_id || asset.id}
                                asset={asset}
                                isFlashing={flashIds.has(String(asset.asset_id || asset.id))}
                                onClick={setSelectedAsset}
                                isSelected={selectedAsset?.asset_id === asset.asset_id}
                                viewMode={viewMode}
                                isComparing={comparisonMode}
                                onToggleCompare={a => setComparedAssets(prev =>
                                  prev.find(x => (x.asset_id || x.id) === (a.asset_id || a.id))
                                    ? prev.filter(x => (x.asset_id || x.id) !== (a.asset_id || a.id))
                                    : [...prev, a]
                                )}
                              />
                            ))}
                          </AnimatePresence>
                        </VStack>
                      </motion.div>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Flex direction="column" align="center" justify="center" h="200px" color="gray.400">
                  <Icon as={FaSearch} boxSize={8} mb={2} />
                  <Text>No assets found matching your filters.</Text>
                  <Button size="sm" mt={2} variant="ghost" colorScheme="blue" onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}>
                    Clear Filters
                  </Button>
                </Flex>
              )}
            </Box>

            {/* ── 🚀 Enhanced pagination with jump-to-page ─────────────────── */}
            {filteredAssets.length > 0 && (
              <MotionBox initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} bg={cardBg} p={3} borderRadius="lg" shadow="md" mt={2} flexShrink={0}>
                <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                  <HStack spacing={1}>
                    <IconButton icon={<FaChevronLeft />} size="xs" isDisabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} variant="ghost" aria-label="Prev" />
                    <HStack spacing={1}>
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                        if (pageNum > totalPages) return null;
                        return (
                          <Button key={pageNum} size="xs" variant={currentPage === pageNum ? 'solid' : 'ghost'} colorScheme={currentPage === pageNum ? 'blue' : 'gray'} onClick={() => handlePageChange(pageNum)}>
                            {pageNum}
                          </Button>
                        );
                      })}
                      {totalPages > 5 && <Text fontSize="xs" color={mutedColor}>…{totalPages}</Text>}
                    </HStack>
                    <IconButton icon={<FaChevronRight />} size="xs" isDisabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} variant="ghost" aria-label="Next" />
                  </HStack>

                  <HStack spacing={2}>
                    {/* 🚀 Jump-to-page input */}
                    <HStack spacing={1}>
                      <Text fontSize="xs" color={mutedColor} whiteSpace="nowrap">Go to</Text>
                      <Input
                        size="xs"
                        w="42px"
                        textAlign="center"
                        value={jumpPage}
                        onChange={e => setJumpPage(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            const p = parseInt(jumpPage, 10);
                            if (!isNaN(p)) handlePageChange(p);
                            setJumpPage('');
                          }
                        }}
                        placeholder={currentPage}
                        borderRadius="md"
                      />
                    </HStack>
                    <Select size="xs" width="75px" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Select>
                  </HStack>
                </Flex>
                <Text fontSize="xs" color={mutedColor} mt={1} textAlign="center">
                  Page {currentPage} of {totalPages} · {filteredAssets.length} assets
                </Text>
              </MotionBox>
            )}
          </GridItem>

          {/* ── RIGHT PANEL: DETAIL VIEW ──────────────────────────────────── */}
          <GridItem minH={0}>
            <MotionBox bg={cardBg} borderRadius="xl" shadow="lg" p={6} h="full" overflow="auto" className="custom-scrollbar" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
              {selectedAsset ? (
                <AssetDetailView asset={selectedAsset} comparisonMode={comparisonMode} comparedAssets={comparedAssets} />
              ) : (
                <Flex direction="column" justify="center" align="center" h="100%" color="gray.400">
                  <MotionBox animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
                    <Box p={6} bg="gray.50" borderRadius="full" mb={4}>
                      <Icon as={FaChartLine} boxSize={10} color="gray.300" />
                    </Box>
                  </MotionBox>
                  <Text fontSize="lg" fontWeight="medium">Select an asset to view predictive insights</Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>{assets.length} assets available for analysis</Text>
                  <HStack mt={3} fontSize="xs" color="gray.400">
                    <Icon as={FaKeyboard} />
                    <Text>Use ↑ ↓ arrow keys to navigate the asset list</Text>
                  </HStack>
                </Flex>
              )}
            </MotionBox>
          </GridItem>
        </Grid>

      </Container>

      {/* ── SETTINGS DRAWER ─────────────────────────────────────────────── */}
      <SettingsDrawer isOpen={isSettingsOpen} onClose={onSettingsClose} settings={settings} onSettingsChange={handleSettingsChange} />

      {/* ── EXPORT MODAL ────────────────────────────────────────────────── */}
      <Modal isOpen={isExportOpen} onClose={onExportClose} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader><HStack><Icon as={FaFileExport} /><Text>Export Asset Data</Text></HStack></ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">Export {filteredAssets.length} filtered assets</Text>
              <Button leftIcon={<Icon as={FaFileDownload}    />} onClick={() => handleExport('csv')}  colorScheme="blue"   variant="outline" justifyContent="flex-start">Export as CSV</Button>
              <Button leftIcon={<Icon as={FaCloudUploadAlt} />} onClick={() => handleExport('json')} colorScheme="purple" variant="outline" justifyContent="flex-start">Export as JSON</Button>
            </VStack>
          </ModalBody>
          <ModalFooter><Button variant="ghost" onClick={onExportClose}>Cancel</Button></ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

AssetHealthCard.propTypes = {
  asset:           PropTypes.object.isRequired,
  onClick:         PropTypes.func.isRequired,
  isSelected:      PropTypes.bool,
  viewMode:        PropTypes.string,
  isComparing:     PropTypes.bool,
  onToggleCompare: PropTypes.func,
  isFlashing:      PropTypes.bool,
};
AiPredictionLine.propTypes = { asset: PropTypes.object.isRequired };
PredictiveHealthDashboard.propTypes = {
  liveVersion:   PropTypes.number,
  lastLiveEvent: PropTypes.object,
  onAskAI:       PropTypes.func,
};
AssetDetailView.propTypes = {
  asset:          PropTypes.object.isRequired,
  comparisonMode: PropTypes.bool,
  comparedAssets: PropTypes.array,
};
SettingsDrawer.propTypes = {
  isOpen:           PropTypes.bool.isRequired,
  onClose:          PropTypes.func.isRequired,
  settings:         PropTypes.object.isRequired,
  onSettingsChange: PropTypes.func.isRequired,
};
CriticalAlertBanner.propTypes = {
  count:      PropTypes.number.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onDismiss:  PropTypes.func.isRequired,
};
LiveConnectionBadge.propTypes = {
  status: PropTypes.oneOf(Object.values(WS_STATUS)).isRequired,
};
HealthDistributionBar.propTypes = {
  healthy:  PropTypes.number.isRequired,
  warning:  PropTypes.number.isRequired,
  critical: PropTypes.number.isRequired,
  total:    PropTypes.number.isRequired,
};
StatusFilterButtons.propTypes = {
  value:    PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  counts:   PropTypes.object.isRequired,
};
SortableTh.propTypes = {
  label:     PropTypes.string.isRequired,
  field:     PropTypes.string.isRequired,
  sortBy:    PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onSort:    PropTypes.func.isRequired,
  isNumeric: PropTypes.bool,
  tooltip:   PropTypes.string,
};

export default PredictiveHealthDashboard;